import { useWorkerGeoLocation } from "@src/appV2/Worker/useWorkerGeoLocation";
import constate from "constate";
import { sortBy } from "lodash";
import { useState } from "react";
import { generatePath, useLocation } from "react-router-dom";

import { type CaseData, type CaseHistoryData, type VisitOccurrenceData } from "../InHome/types";
import { useGetVisit } from "../InHome/useGetVisitData";
import { getCaseHistoryModalRouteParams } from "./getCaseHistoryModalRouteParams";
import { type InHomeCaseHistoryModalRoutePathParams } from "./types";

interface UseInHomeCaseHistoryDataProps {
  baseNavigationPath: string;
}

interface UseInHomeCaseHistoryDataContext {
  baseNavigationPath: string;
  generateModalPath: <S extends string>(
    path: S,
    params?: Parameters<typeof generatePath<S>>[1]
  ) => string;
  modalRouteParams: InHomeCaseHistoryModalRoutePathParams | undefined;
  caseHistoryData: CaseHistoryData | undefined;
  setCaseHistoryData: (caseHistoryData: CaseHistoryData | undefined) => void;
  visitOccurrences: VisitOccurrenceData[];
  caseData: CaseData | undefined;
  isVisitLoading: boolean;
}

function useInHomeCaseHistoryData(
  props: UseInHomeCaseHistoryDataProps
): UseInHomeCaseHistoryDataContext {
  const { baseNavigationPath } = props;
  const { pathname } = useLocation();
  const modalRouteParams = getCaseHistoryModalRouteParams(baseNavigationPath, pathname);

  const [caseHistoryData, setCaseHistoryData] = useState<CaseHistoryData | undefined>(undefined);

  const visitId = modalRouteParams?.visitId;
  const caseId = modalRouteParams?.caseId;
  const geoLocation = useWorkerGeoLocation();

  const { data: visitData, isLoading: isVisitLoading } = useGetVisit({
    visitId,
    caseId,
    geoLocation,
    includeOccurrences: true,
  });

  const visitOccurrences =
    visitData?.included?.filter((item) => item.type === "visit-occurrence") ?? [];
  const caseData = visitData?.included?.find((item) => item.type === "case");

  const generateModalPath = <S extends string>(
    path: S,
    params?: Parameters<typeof generatePath<S>>[1]
  ) => {
    return generatePath(
      `${baseNavigationPath}/${path}`,
      params as unknown as Parameters<typeof generatePath<`${string}/${S}`>>[1]
    );
  };

  return {
    baseNavigationPath,
    generateModalPath,
    modalRouteParams,
    caseHistoryData,
    setCaseHistoryData,
    visitOccurrences: sortBy(visitOccurrences, "attributes.completedAt").reverse(),
    caseData,
    isVisitLoading,
  };
}

export const [InHomeCaseHistoryDataProvider, useInHomeCaseHistoryDataContext] =
  constate(useInHomeCaseHistoryData);
